// "New conversation" drawer: pick an existing executor connection (07-chat.md
// §2.7 "Verbindingkeuze: alleen executor_cli-verbindingen die claude-cli of
// pi kunnen draaien") and, optionally, a model. The title is written by the assistant after the first message. Only offers a
// connection whose `allowedRunners` (runner-resolution.ts, via
// executor-connections.service.ts) includes `claude-cli` or `pi` — a
// Codex-with-ChatGPT-login connection (`codex-cli` only) can't run a chat
// turn at all, and `createConversation` would 422 on it anyway (07 QA #14);
// filtering it out here just avoids that round trip.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ChatConversation } from '@goblic/shared'
import { chatApi, connectionsApi } from '../../lib/api-client'
import { useAuth } from '../../lib/auth'
import { Drawer, Field } from '../ui'
import { ModelPicker } from '../model-picker'

export function NewConversationForm({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (conversation: ChatConversation) => void
}) {
  const { user } = useAuth()
  const workspaceId = user?.workspaceId ?? ''
  const [credentialId, setCredentialId] = useState('')
  const [model, setModel] = useState('')
  const [confirmWrites, setConfirmWrites] = useState(true)

  const connectionsQuery = useQuery({
    queryKey: ['executor-connections', workspaceId],
    queryFn: () => connectionsApi.list(workspaceId),
    enabled: open && workspaceId !== '',
  })
  const connections = (connectionsQuery.data?.items ?? []).filter(
    (connection) => connection.allowedRunners.includes('claude-cli') || connection.allowedRunners.includes('pi'),
  )

  const queryClient = useQueryClient()
  const create = useMutation({
    mutationFn: () =>
      chatApi.createConversation({
        credentialId,
        model: model.trim() || undefined,
        confirmWrites,
      }),
    onSuccess: (conversation) => {
      void queryClient.invalidateQueries({ queryKey: ['chat-conversations'] })
      reset()
      onCreated(conversation)
    },
  })

  function reset(): void {
    setCredentialId('')
    setModel('')
    setConfirmWrites(true)
  }

  function close(): void {
    reset()
    onClose()
  }

  return (
    <Drawer
      open={open}
      onClose={close}
      title="New conversation"
      description="Talk with the Goblic assistant through one of your connections."
      footer={
        <>
          <button type="button" className="btn-secondary btn-sm" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={credentialId === '' || create.isPending}
            onClick={() => create.mutate()}
          >
            Start conversation
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <Field label="Connection" htmlFor="chat-connection">
          {connectionsQuery.isLoading ? (
            <p className="text-base text-ink-3">Loading connections…</p>
          ) : connections.length === 0 ? (
            <p className="text-base text-ink-3">
              No executor connections yet — add one on the Executors page
              first.
            </p>
          ) : (
            <select
              id="chat-connection"
              className="field-input"
              value={credentialId}
              onChange={(event) => setCredentialId(event.target.value)}
            >
              <option value="">Choose a connection…</option>
              {connections.map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Model" htmlFor="chat-model" hint="Empty uses the connection's default.">
          <ModelPicker
            id="chat-model"
            workspaceId={workspaceId}
            target={credentialId ? { connectionId: credentialId } : null}
            value={model}
            onChange={setModel}
          />
        </Field>

        <label className="flex items-center justify-between gap-3 rounded-control border border-line px-3.5 py-2.5">
          <span>
            <span className="block text-base font-medium text-ink">
              Confirm changes before they run
            </span>
            <span className="block text-sm text-ink-3">
              Deleting something always asks, regardless of this switch.
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            className="switch shrink-0"
            checked={confirmWrites}
            onChange={(event) => setConfirmWrites(event.target.checked)}
          />
        </label>

        {create.isError ? (
          <p className="text-sm text-danger">
            {create.error instanceof Error ? create.error.message : 'Could not start that conversation.'}
          </p>
        ) : null}
      </div>
    </Drawer>
  )
}
